/* eslint-disable sonarjs/cognitive-complexity */
import { useEffect, useRef, useState } from 'react';

export const measureTextHeight = (fontSize: number): number => {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');

  if (context) {
    context.font = `${fontSize}px`;

    const metrics = context.measureText('M');

    return metrics.actualBoundingBoxAscent + metrics.actualBoundingBoxDescent;
  }
  return 0;
};
export const measureTextWidth = (
  text: string,
  fontSize: number,
  fontFamily: string,
  fontWeight: string,
): number => {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');

  if (context) {
    context.font = `${fontWeight} ${fontSize}px ${fontFamily}`;
    const metrics = context.measureText(text);

    return metrics.width;
  }
  return 0;
};

export const calculateFontSizeForText = (
  text: string,
  containerWidth: number,
  viewportWidth: number,
  maxContentWidth: number,
  isNoWrap: boolean, // Flag for nowrap class
  fontFamily: string,
  fontWeight: string,
): number => {
  const baseMaxFontSize = 175; // Base max font size
  const minFontSize = 10; // Minimum font size to avoid being too small

  // Adjust font size based on viewport width dynamically
  const responsiveMaxFontSize = Math.min(
    baseMaxFontSize,
    (viewportWidth / 8) * (1 - Math.min(viewportWidth / 1000, 0.35)), // Dynamic scaling factor
  );

  // Adjust font size based on character length
  const characterFactor = Math.max(text.length / 20, 1); // Example factor, adjust as needed
  let fontSize = responsiveMaxFontSize / characterFactor;

  // Additional scaling for longer character counts
  if (text.length > 20) {
    fontSize *= 0.8; // Scale down by 20% for longer text
  }

  let textWidth = measureTextWidth(text, fontSize, fontFamily, fontWeight);

  // Adjust font size more aggressively when `noWrapClass` is applied and text exceeds max content width

  if (isNoWrap && textWidth > maxContentWidth) {
    fontSize *= maxContentWidth / textWidth;
    textWidth = measureTextWidth(text, fontSize, fontFamily, fontWeight);
  }

  // Further adjust font size if the text still exceeds the container or max content width
  while (
    (textWidth > containerWidth || textWidth > maxContentWidth) &&
    fontSize > minFontSize
  ) {
    fontSize -= 1; // Decrease in smaller increments for finer control
    textWidth = measureTextWidth(text, fontSize, fontFamily, fontWeight);
  }

  // Ensure the final font size is not larger than the responsive max font size
  fontSize = Math.min(fontSize, responsiveMaxFontSize);

  return fontSize;
};

const useDynamicFontSize = (businessName: string) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [fontSize, setFontSize] = useState<number>(0);
  const [containerHeight, setContainerHeight] = useState<number>(0);
  const [nowrapClass, setNowrapClass] = useState<string>(''); // State for nowrap class

  const maxContentWidth = 1497; // Define your max content width

  useEffect(() => {
    const updateLayout = () => {
      requestAnimationFrame(() => {
        const container = containerRef.current;

        if (container) {
          const containerWidth = container.offsetWidth || 0;
          const viewportWidth = window.innerWidth;

          if (containerWidth > 0) {
            // Calculate dynamic character threshold based on container width
            const dynamicThreshold = Math.max(
              14, // Minimum threshold
              Math.floor(containerWidth / 100), // Example calculation
            );

            // Determine if nowrap class should be applied
            const isNoWrap = businessName.length <= dynamicThreshold;

            setNowrapClass(isNoWrap ? 'noWrapClass' : '');

            const calculatedFontSize = calculateFontSizeForText(
              businessName,
              containerWidth,
              viewportWidth,
              maxContentWidth, // Pass the max content width here
              isNoWrap, // Pass the nowrap flag
              'Archivo', // Default font-family (replace as necessary)
              'normal', // Default font-weight (replace as necessary)
            );

            setFontSize(calculatedFontSize);

            // Calculate required container height
            const textHeight = measureTextHeight(calculatedFontSize);
            const requiredHeight = textHeight + 100; // Add extra height for padding or margins

            setContainerHeight(requiredHeight);
          }
        }
      });
    };

    const containerElement = containerRef.current; // Store ref value in a variable

    // Add ResizeObserver to handle dynamic resizing
    const resizeObserver = new ResizeObserver(() => {
      updateLayout();
    });

    if (containerElement) {
      resizeObserver.observe(containerElement);
    }

    // Listen to viewport resize
    window.addEventListener('resize', updateLayout);

    // Initial update
    updateLayout();

    // Cleanup observer and event listener on component unmount
    return () => {
      if (containerElement) {
        resizeObserver.unobserve(containerElement);
      }
      window.removeEventListener('resize', updateLayout);
    };
  }, [businessName]);

  return { containerRef, fontSize, containerHeight, nowrapClass };
};

export default useDynamicFontSize;
